import React, { useContext, useEffect, useRef, useState } from 'react'
import { com, kotlin } from '@eidu/form'
import { Alert, Button, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import DraftFieldValue, { fieldValueToDraftFieldValue } from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import { requireNotUndefinedOrNull } from '../../util/require'
import useEntityRepository from '../../io/useEntityRepository'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import { isNotUndefinedOrNull } from '../../util/predicates'
import getFormContent from '../../api/form/getFormContent'
import usePermittedActions from '../../io/usePermittedActions'
import EntityNotAccessibleError from './EntityNotAccessibleError'
import EntityFormDisplay from '../../components/data/EntityFormDisplay'
import LoginIdentifierField from '../../components/input/LoginIdentifierField'
import FormContentInput from '../../components/input/FormContentInput'
import PageHeaderActionLabel, { pageHeaderActionButtonSx } from '../../components/layout/PageHeaderActionLabel'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { isManualOrderField } from '../../util/entityType/orderField'
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import Entity = com.eidu.sharedlib.entity.Entity
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import getFormContentId = com.eidu.sharedlib.entity.getFormContentId
import PermittedEntityAction = com.eidu.sharedlib.entity.permission.PermittedEntityAction
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import KtList = kotlin.collections.KtList
import linkKeyFromString = com.eidu.sharedlib.entity.link.linkKeyFromString
import Page = com.eidu.sharedlib.form.definition.Page
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import LearningUnitEditorWrapper from '../../ui/microfrontend/LearningUnitEditorWrapper'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EntityTypeNotAccessibleError from './EntityTypeNotAccessibleError'

const getNonNullFieldsFromEntity = (entity: Entity): ReadonlyMap<FieldId, DraftFieldValue> =>
  new EqualityHashMap<FieldId, DraftFieldValue>(
    Array.from(entity.valuesByFieldId.asJsReadonlyMapView())
      .filter((it): it is [FieldId, FieldValue] => it[1] !== null)
      .map(([id, value]) => [id, fieldValueToDraftFieldValue(id, value)])
  )

export type ReadyViewPageProps = {
  entityId: EntityId
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyViewPage = ({ entityId, entityType, entityTypes }: ReadyViewPageProps) => {
  const entityRepository = useEntityRepository(
    {
      typeId: entityType.id,
      types: entityTypes,
    },
    [entityType.id.toString(), entityTypes.size]
  )

  // Important: entityId must not change between undefined and
  // not undefined, otherwise React's hook order will be messed up.
  const entity = entityRepository.useItem(entityId)

  const navigate = useNavigate()
  const authContext = createAuthContext(useContext<IAuthContext>(AuthContext))

  const [formContentLoading, setFormContentLoading] = useState(false)

  // Important: all use* need to run, so don't replace this by anything using short-circuiting!
  const error = [entityRepository.useError(), entity === null ? 'Entity not found' : undefined]
    .find((it) => !!it)
    ?.let((it) => String(it))
  const loading = [entityRepository.useLoading(), entity === undefined].some((it) => it) || formContentLoading

  const [formContent, setFormContent] = useState<KtList<Page>>(KtList.fromJsArray([]))

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')

  const requested = useRef([
    new PermittedEntityAction(EntityAction.UpdateEntity, entityId),
    new PermittedEntityAction(EntityAction.ReadEntity, entityId),
  ])
  const [permissions] = usePermittedActions(requested.current)

  const hasUpdatePermission = permissions?.some(
    (permittedAction) =>
      permittedAction instanceof PermittedEntityAction &&
      permittedAction.target.equals(entityId) &&
      permittedAction.action === EntityAction.UpdateEntity
  )

  const loadingOverlayOpen = loading && !error

  useEffect(() => {
    if (entity) {
      setFields(getNonNullFieldsFromEntity(entity.entity))

      const externalValues = entity.entity.externalValues?.asJsReadonlyMapView() ?? new Map()
      setPhone(externalValues.get(phoneNumberLoginIdentifierKey) || '')
      setEmail(externalValues.get(emailLoginIdentifierKey) || '')

      const formContentId = getFormContentId(entity.entity) ?? undefined
      if (isNotUndefinedOrNull(formContentId)) {
        setFormContentLoading(true)
        getFormContent({
          formId: entity.entity.id.toString(),
          formContentId: formContentId.toString(),
          authContext: requireNotUndefinedOrNull(authContext),
        }).then((content) => {
          const formContentWithPageKeys = KtList.fromJsArray(
            content
              .asJsReadonlyArrayView()
              .map((page: Page, index: number) =>
                !page.key ? page.copy(page.title, page.items, `page_${Date.now()}_${index}`) : page
              )
          )
          setFormContent(formContentWithPageKeys)
          setFormContentLoading(false)
        })
      } else {
        setFormContent(KtList.fromJsArray([]))
      }
    }
  }, [entity])

  const navigateToEdit = () => {
    navigate(`/entities/${entityType.id.toString()}/${entityId.toString()}/edit`)
  }

  return (
    <Stack>
      {entity !== null && entityType && (
        <Stack padding={3} spacing={3}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h4">{getTypeDisplayName(entityType.name)}</Typography>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'right' }}>
              {entity && hasUpdatePermission && (
                <Button
                  color="primary"
                  startIcon={<EditOutlinedIcon />}
                  onClick={navigateToEdit}
                  aria-label="Edit"
                  sx={pageHeaderActionButtonSx}
                >
                  <PageHeaderActionLabel>EDIT</PageHeaderActionLabel>
                </Button>
              )}
            </Stack>
          </Stack>
          <Stack padding={0} spacing={3}>
            {entityType.fields
              .asJsReadonlyArrayView()
              .filter((field) => !isManualOrderField(field))
              .map((field) => (
                <EntityFormDisplay
                  key={`entity-form-field-${field.id.toString()}`}
                  field={field}
                  fieldValue={fields.get(field.id)}
                />
              ))}
          </Stack>
          {entityType.kind === EntityTypeKind.User && <LoginIdentifierField phone={phone} email={email} disabled />}
          {entityType.kind === EntityTypeKind.Form &&
            ((formContent?.asJsReadonlyArrayView().every((page) => page.key) && (
              <FormContentInput pages={formContent} />
            )) ||
              (!loading && (
                <Alert severity={'error'}>
                  Some pages on this form do not have a key set. This is not supported. Contact EIDU if the issue
                  persists.
                </Alert>
              )))}
          {entityType.kind === EntityTypeKind.LearningUnit && entity && (
            <LearningUnitEditorWrapper
              entityId={entity.entity.id.toString()}
              entityLinks={[...(entity.entity.links.asJsReadonlyMapView().entries() || [])].map(([key, value]) => ({
                key,
                value,
              }))}
              linkKeyFromString={linkKeyFromString}
              onRegisterPartialSubmit={() => Promise.resolve([])}
              mode="view"
            />
          )}
          <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(-1)}>Back</Button>
          </Stack>
        </Stack>
      )}
      {!entity && !loadingOverlayOpen && <EntityNotAccessibleError />}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </Stack>
  )
}

const ViewEntityPage = () => {
  const params = useParams()
  const entityId = entityIdFromString(requireNotUndefinedOrNull(params.entityId, 'Entity ID is required'))
  const entityTypeId = entityTypeIdFromString(requireNotUndefinedOrNull(params.entityTypeId))

  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const error = entityTypeRepository.useError()?.let((it) => String(it))

  const loadingType = entityTypeRepository.useLoading()
  const loadingOverlayOpen = loadingType && !error

  return (
    <>
      {entityType && entityTypes && (
        <ReadyViewPage entityId={entityId} entityType={entityType} entityTypes={entityTypes} />
      )}
      {(!entityType || !entityTypes) && !loadingOverlayOpen && <EntityTypeNotAccessibleError />}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </>
  )
}

export default ViewEntityPage
